import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { applySnapshot, cast, getSnapshot, types } from 'mobx-state-tree';
import { VACANT_UNIT_ALLOCATIONS, UTILITY_CONFIGURATION_TYPES, BILL_FREQUENCY, ALLOCATION_METHOD, VACANT_UNIT_USAGE, PAID_BY_TO_VACANT_UNIT_ALLOCATIONS } from 'src/constants/utilities';
import { toJS } from 'mobx';
import { isValueNumber } from 'src/utils/typeValidators';
const AmenityOptions = types.model({
  id: types.string,
  name: types.string,
  weight: types.maybeNull(types.number)
});
const CommonAreaAmenities = types.model({
  id: types.optional(types.string, ''),
  commonAreaAmenityId: types.string,
  name: types.string,
  amenitySelectionType: types.maybeNull(types.number),
  amenitySelectedOption: types.maybeNull(types.string),
  amenitySelectedOptionName: types.maybeNull(types.string),
  weight: types.maybeNull(types.number),
  commonAreaAmenityOptions: types.array(AmenityOptions),
  isSaving: types.optional(types.boolean, false),
  isFetched: types.optional(types.boolean, false)
});

// const ProviderUtility = types.model({
//   id: types.string,
//   name: types.string,
// });

const UtilityConfig = types.model({
  id: types.optional(types.string, ''),
  utilityProviderId: types.maybeNull(types.string),
  utilityProviderName: types.maybeNull(types.string),
  utilityProviderWebSite: types.optional(types.string, ''),
  utilityId: types.optional(types.string, ''),
  utilityTypeId: types.optional(types.string, ''),
  // name: types.optional(types.string, ''),
  name: types.maybeNull(types.string),
  billFrequencyType: types.optional(types.string, BILL_FREQUENCY.Monthly),
  allocationMethod: types.optional(types.string, ALLOCATION_METHOD.ByOccupancy),
  defaultAllocationMethod: types.maybeNull(types.string),
  buildingIds: types.optional(types.array(types.string), []),
  utilityCap: types.maybeNull(types.number),
  utilityCAD: types.maybeNull(types.number),
  isDefaultCAD: types.optional(types.boolean, false),
  goodWillDeduction: types.maybeNull(types.optional(types.number, 5)),
  vacantUnitAllocation: types.optional(types.string, VACANT_UNIT_ALLOCATIONS.DivideAmongOccupied),
  defaultVacantUnitAllocation: types.maybeNull(types.string),
  vacantUnitUsage: types.optional(types.string, VACANT_UNIT_USAGE.NoUsage),
  vacantUnitUsageLimit: types.maybeNull(types.number),
  vacantUnitOccupantsCount: types.maybeNull(types.number),
  defaultVacantUnitUsage: types.maybeNull(types.string),
  isFetched: types.optional(types.boolean, false),
  isEnabled: types.maybeNull(types.boolean),
  // Only used to keep track of custom created utility at custom provider
  isCustom: types.optional(types.boolean, false),
  // AP Push chosen in the provider setup for a utility not saved yet (LB-6031); turned on once it is saved.
  isApPushEnabled: types.optional(types.boolean, false)
}).actions(self => {
  return {
    updateKeyValue(key, value) {
      self[key] = value;
    }
  };
});
const UtilityProvider = types.model({
  id: types.string,
  utilityProviderId: types.maybeNull(types.string),
  // Declared or MST strips it, and the credential gate reads it as "no scraper covers this".
  scraperProviderKey: types.maybeNull(types.string),
  name: types.string,
  website: types.optional(types.maybeNull(types.string), ''),
  utilities: types.array(UtilityConfig),
  billsDownloadNeeded: types.optional(types.boolean, false),
  isAppAutomationEnabled: types.maybeNull(types.boolean),
  isCustom: types.optional(types.boolean, false),
  isFetched: types.optional(types.boolean, false)
});
const UtilityConfigurator = types.model({
  providerName: types.string,
  providerId: types.string,
  // Canonical scraper key; presence gates the credential form. Null when no scraper covers it.
  scraperProviderKey: types.maybeNull(types.string),
  utilityProviderWebSite: types.optional(types.string, ''),
  utilities: types.array(UtilityConfig)
});
const PropertyUtilitySetupByProviderHolder = types.model({
  providerUtilitiesLinkId: types.string,
  utilities: types.optional(types.array(UtilityConfig), [])
});
const PropertyUtilityModel = types.model({
  propertyUtilityId: types.string,
  provider: types.model({
    utilityProviderId: types.string,
    utilityProviderName: types.string,
    billsDownloadNeeded: types.boolean,
    scraperProviderKey: types.maybeNull(types.string)
  }),
  utilityTypes: types.array(types.model({
    utilityTypeId: types.string,
    utilityTypeName: types.string
  })),
  billFrequencyType: types.string,
  allocationMethod: types.string,
  commonAreaDeduction: types.number,
  vacantUnitAllocation: types.string,
  vacantUnitUsage: types.string,
  vacantUnitUsageLimit: types.number,
  vacantUnitOccupantsCount: types.maybeNull(types.number),
  goodWillDeduction: types.number
});
const PropertyUtilityGl = types.model({
  propertyUtilityLivableId: types.maybeNull(types.string),
  glAccountExternalId: types.maybeNull(types.string)
});
const AssociatedVendor = types.model({
  id: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  number: types.maybeNull(types.string)
});
export const UtilitiesSetupV2 = types.model({
  sqFootage: types.optional(types.string, ''),
  utilityProviders: types.optional(types.array(UtilityProvider), []),
  ["".concat(UTILITY_CONFIGURATION_TYPES.UtilityConfigurator)]: types.maybeNull(UtilityConfigurator),
  selectedUtilities: types.optional(types.array(UtilityConfig), []),
  customUtilities: types.optional(types.array(UtilityConfig), []),
  propertyUtilitySetupByProvider: types.optional(types.array(PropertyUtilitySetupByProviderHolder), []),
  // NOTE: Used only to create new custom Provider
  ["".concat(UTILITY_CONFIGURATION_TYPES.CustomUtilityProviderConfigurator)]: types.maybeNull(UtilityConfigurator),
  // NOTE: Used only to edit custom Provider
  ["".concat(UTILITY_CONFIGURATION_TYPES.EditCustomUtilityProviderConfigurator)]: types.maybeNull(UtilityConfigurator),
  utilityProvidersToDelete: types.optional(types.array(UtilityProvider), []),
  selectedUtilitiesToDelete: types.optional(types.array(UtilityConfig), []),
  // NOTE: Amenities related stuff
  selectedAmenitiesToDelete: types.optional(types.array(CommonAreaAmenities), []),
  availableCommonAreaAmenities: types.optional(types.array(CommonAreaAmenities), []),
  selectedAmenities: types.optional(types.array(CommonAreaAmenities), []),
  propertyUtilities: types.optional(types.array(PropertyUtilityModel), []),
  rentGlAccountExternalId: types.optional(types.string, ''),
  globalGlAccountExternalId: types.optional(types.string, ''),
  accountsPayableGlAccountExternalId: types.optional(types.string, ''),
  propertyUtilityGlAccounts: types.optional(types.array(PropertyUtilityGl), []),
  associatedVendorInAppFolio: types.optional(types.string, ''),
  associatedVendorsInAppFolio: types.optional(types.array(AssociatedVendor), [{
    id: '1',
    name: 'lorem1',
    number: 'num1'
  }, {
    id: '2',
    name: 'lorem2',
    number: 'num2'
  }, {
    id: '3',
    name: 'lorem3',
    number: 'num3'
  }]),
  associatedVendorsInAppFolioStored: types.optional(types.array(AssociatedVendor), [{
    id: '1',
    name: 'lorem1',
    number: 'num1'
  }, {
    id: '2',
    name: 'lorem2',
    number: 'num2'
  }, {
    id: '3',
    name: 'lorem3',
    number: 'num3'
  }])
}).actions(store => {
  const initialState = getSnapshot(store);
  let afterLoadState;
  return {
    setAssociatedVendorInAppFolio(data) {
      store.associatedVendorInAppFolio = data;
    },
    resetAssociatedVendorsInAppFolio() {
      store.associatedVendorsInAppFolio = toJS(store.associatedVendorsInAppFolioStored);
    },
    setAssociatedVendorsInAppFolio(data) {
      store.associatedVendorsInAppFolio = data;
    },
    resetStoreToInitValues() {
      try {
        applySnapshot(store, initialState);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    initAfterLoadState() {
      try {
        afterLoadState = getSnapshot(store);
      } catch (error) {
        console.error('STORE ERROR > initAfterLoadState > ', error);
      }
    },
    applyAfterLoadState() {
      try {
        applySnapshot(store, afterLoadState);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    changeSqFootage(data) {
      try {
        store.sqFootage = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    /*
       ########### Utilities and Providers
     */
    updateUtilsDefaults(utilityInfosResp) {
      try {
        var _utilityInfosResp$res;
        if (utilityInfosResp && !utilityInfosResp.isSuccessful) {
          return;
        }
        const getKeysToUpdate = (utilityInfoData, utilityFetched) => {
          if (utilityInfoData === undefined) return null;
          const fieldsToUpdate = {
            isDefaultCAD: (utilityInfoData === null || utilityInfoData === void 0 ? void 0 : utilityInfoData.cad) !== null && (utilityInfoData === null || utilityInfoData === void 0 ? void 0 : utilityInfoData.cad),
            defaultAllocationMethod: ALLOCATION_METHOD[utilityInfoData.defaultAllocationMethod] || null,
            defaultVacantUnitAllocation: PAID_BY_TO_VACANT_UNIT_ALLOCATIONS[utilityInfoData.paidBy] || null,
            defaultVacantUnitUsage: VACANT_UNIT_USAGE[utilityInfoData.defaultVacantUnitUsage] || null
          };
          if (!utilityFetched) {
            fieldsToUpdate.utilityCAD = utilityInfoData !== null && utilityInfoData !== void 0 && utilityInfoData.cad ? 5 : 0;
            fieldsToUpdate.allocationMethod = ALLOCATION_METHOD[utilityInfoData.defaultAllocationMethod] || ALLOCATION_METHOD.ByOccupancy;
            fieldsToUpdate.vacantUnitAllocation = PAID_BY_TO_VACANT_UNIT_ALLOCATIONS[utilityInfoData.paidBy];
            fieldsToUpdate.vacantUnitUsage = VACANT_UNIT_USAGE[utilityInfoData.defaultVacantUnitUsage];
          }
          return fieldsToUpdate;
        };
        const utilityInfos = utilityInfosResp === null || utilityInfosResp === void 0 || (_utilityInfosResp$res = utilityInfosResp.response) === null || _utilityInfosResp$res === void 0 ? void 0 : _utilityInfosResp$res.utilities.reduce((acc, item) => {
          acc[item.utilityId] = _objectSpread({}, item);
          return acc;
        }, {});
        Object.values(UTILITY_CONFIGURATION_TYPES).forEach(configuratorType => {
          var _store$configuratorTy;
          if (store[configuratorType] && (_store$configuratorTy = store[configuratorType]) !== null && _store$configuratorTy !== void 0 && _store$configuratorTy.utilities) {
            var _store$configuratorTy2;
            (_store$configuratorTy2 = store[configuratorType]) === null || _store$configuratorTy2 === void 0 || _store$configuratorTy2.utilities.forEach((util, index) => {
              const keysToUpdate = getKeysToUpdate(utilityInfos[util.utilityId], util === null || util === void 0 ? void 0 : util.isFetched);
              if (keysToUpdate !== null) Object.entries(keysToUpdate).forEach(_ref => {
                let _ref2 = _slicedToArray(_ref, 2),
                  key = _ref2[0],
                  value = _ref2[1];
                store[configuratorType].utilities[index].updateKeyValue(key, value);
              });
            });
          }
        });
        if (store.utilityProviders && store.utilityProviders.length) {
          store.utilityProviders.forEach((provider, providerIndex) => {
            if (provider && provider.utilities) {
              provider === null || provider === void 0 || provider.utilities.forEach((util, utilIndex) => {
                const keysToUpdate = getKeysToUpdate(utilityInfos[util.utilityId], util.isFetched);
                if (keysToUpdate !== null) Object.entries(keysToUpdate).forEach(_ref3 => {
                  let _ref4 = _slicedToArray(_ref3, 2),
                    key = _ref4[0],
                    value = _ref4[1];
                  store.utilityProviders[providerIndex].utilities[utilIndex].updateKeyValue(key, value);
                });
              });
            }
          });
        }
        if (store.selectedUtilities.length) {
          store.selectedUtilities.forEach((selectedUtility, index) => {
            if (selectedUtility) {
              const keysToUpdate = getKeysToUpdate(utilityInfos[selectedUtility.utilityId], selectedUtility.isFetched);
              if (keysToUpdate !== null) Object.entries(keysToUpdate).forEach(_ref5 => {
                let _ref6 = _slicedToArray(_ref5, 2),
                  key = _ref6[0],
                  value = _ref6[1];
                store.selectedUtilities[index].updateKeyValue(key, value);
              });
            }
          });
        }
      } catch (error) {
        console.log('STORE ERROR > updateUtilsDefaults', error);
      }
    },
    addProvider(data, storeAsFetched) {
      try {
        const existingIndex = store.utilityProviders.findIndex(provider => provider.id === data.id);
        if (existingIndex === -1) {
          store.utilityProviders.push(_objectSpread(_objectSpread({}, data), {}, {
            name: data.name || data.utilityProviderName,
            utilities: data.utilities.map(util => {
              return _objectSpread(_objectSpread({}, util), {}, {
                utilityId: (util === null || util === void 0 ? void 0 : util.utilityId) || util.id
              });
            }),
            isFetched: storeAsFetched
          }));
        }
        if (existingIndex !== -1) {
          Object.entries(data).forEach(_ref7 => {
            let _ref8 = _slicedToArray(_ref7, 2),
              key = _ref8[0],
              value = _ref8[1];
            if (key === 'utilityProviderName') {
              store.utilityProviders[existingIndex].name = value;
            } else {
              store.utilityProviders[existingIndex][key] = value;
            }
          });
        }
      } catch (error) {
        console.error('STORE ERROR > addProvider', error);
      }
    },
    removeProvider(data) {
      try {
        store.selectedUtilities.map(selectedUtility => {
          if (selectedUtility.utilityProviderId === data.id) {
            store.selectedUtilities.splice(store.selectedUtilities.findIndex(utility => utility.utilityProviderId === data.id), 1);
          }
        });
        store.utilityProviders.map((selectedUtilityProviders, index) => {
          if (selectedUtilityProviders.id === data.id) {
            store.utilityProviders.splice(index, 1);
          }
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setNewUtilityConfiguration(data) {
      let configuratorType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : UTILITY_CONFIGURATION_TYPES.UtilityConfigurator;
      // TODO: will be added default utility setup
      try {
        var _data$scraperProvider;
        store["".concat(configuratorType)] = cast({
          providerId: data.id,
          providerName: data.name,
          scraperProviderKey: (_data$scraperProvider = data.scraperProviderKey) !== null && _data$scraperProvider !== void 0 ? _data$scraperProvider : null,
          utilityProviderWebSite: data.website || data.utilityProviderWebSite,
          utilities: data.utilities.map(utility => {
            var _utility$vacantUnitOc;
            return _objectSpread(_objectSpread({}, utility), {}, {
              id: utility.id,
              utilityProviderId: utility.utilityProviderId || data.id,
              utilityProviderName: utility.utilityProviderName || data.name,
              utilityProviderWebSite: data.website || data.utilityProviderWebSite,
              utilityId: utility.utilityId || utility.id,
              name: utility.utilityType || utility.name,
              billFrequencyType: BILL_FREQUENCY[utility.billFrequencyType] || utility.billFrequencyType,
              allocationMethod: ALLOCATION_METHOD[utility.allocationMethod] || utility.allocationMethod,
              buildingIds: utility.buildingIds,
              utilityCap: isValueNumber(utility.utilityCap) ? utility.utilityCap : 0,
              utilityCAD: isValueNumber(utility === null || utility === void 0 ? void 0 : utility.utilityCAD) ? utility === null || utility === void 0 ? void 0 : utility.utilityCAD : 5,
              goodWillDeduction: isValueNumber(utility === null || utility === void 0 ? void 0 : utility.goodWillDeduction) ? utility === null || utility === void 0 ? void 0 : utility.goodWillDeduction : 5,
              vacantUnitAllocation: utility === null || utility === void 0 ? void 0 : utility.vacantUnitAllocation,
              vacantUnitUsage: utility === null || utility === void 0 ? void 0 : utility.vacantUnitUsage,
              vacantUnitOccupantsCount: (_utility$vacantUnitOc = utility === null || utility === void 0 ? void 0 : utility.vacantUnitOccupantsCount) !== null && _utility$vacantUnitOc !== void 0 ? _utility$vacantUnitOc : null,
              isEnabled: (utility === null || utility === void 0 ? void 0 : utility.isEnabled) || false,
              isFetched: (utility === null || utility === void 0 ? void 0 : utility.isFetched) || false,
              isCustom: (utility === null || utility === void 0 ? void 0 : utility.isCustom) || false
            });
          })
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    updateNewUtilityConfiguration(data) {
      let configuratorType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : UTILITY_CONFIGURATION_TYPES.UtilityConfigurator;
      try {
        data.forEach(datum => {
          const utility = datum.utility,
            values = datum.values;
          try {
            const selectedUtilityIndex = store[configuratorType].utilities.findIndex(i => configuratorType === UTILITY_CONFIGURATION_TYPES.UtilityConfigurator ? i.utilityProviderId === utility.utilityProviderId && i.utilityId === utility.utilityId : i.utilityId === utility.utilityId);
            if (selectedUtilityIndex !== -1) {
              values.forEach(_ref9 => {
                let key = _ref9.key,
                  value = _ref9.value;
                store[configuratorType].utilities[selectedUtilityIndex].updateKeyValue(key, toJS(value));
              });
            } else {
              if (utility.isCustom) store[configuratorType].utilities.push(toJS(utility));
            }
          } catch (error) {
            console.log('STORE updateNewUtilityConfiguration error', error);
          }
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    /**
     * AP Push chosen for a utility not saved yet (LB-6031): on its selected row, or, until it is selected, on the
     * provider's own, which selecting it copies along. Turned on once the utility is saved.
     */
    setApPushChoice(_ref0, value) {
      var _store$utilityProvide;
      let utilityProviderId = _ref0.utilityProviderId,
        utilityId = _ref0.utilityId;
      const selected = store.selectedUtilities.find(i => i.utilityProviderId === utilityProviderId && i.utilityId === utilityId);
      const offered = (_store$utilityProvide = store.utilityProviders.find(provider => provider.id === utilityProviderId)) === null || _store$utilityProvide === void 0 ? void 0 : _store$utilityProvide.utilities.find(i => (i.utilityId || i.id) === utilityId);
      const target = selected !== null && selected !== void 0 ? selected : offered;
      if (target) target.isApPushEnabled = value;
    },
    updateUtilityConfig(data) {
      try {
        const toDeleteCheck = _ref1 => {
          let selectedUtilityIndex = _ref1.selectedUtilityIndex,
            valueKey = _ref1.valueKey,
            prevValue = _ref1.prevValue,
            newValue = _ref1.newValue,
            utility = _ref1.utility;
          switch (valueKey) {
            case 'isEnabled':
              {
                if (prevValue && !newValue && store.selectedUtilities[selectedUtilityIndex].isFetched) {
                  store.addUtilityToDelete(toJS(store.selectedUtilities[selectedUtilityIndex]));
                }
                if (!prevValue && newValue) {
                  const utilityToDeleteIndex = store.selectedUtilitiesToDelete.findIndex(i => i.utilityProviderId === utility.utilityProviderId && i.utilityId === utility.utilityId);
                  if (selectedUtilityIndex !== -1 && utilityToDeleteIndex !== -1) store.selectedUtilities.splice(utilityToDeleteIndex, 1);
                }
              }
          }
        };
        const handleValues = (_ref10, selectedUtilityIndex) => {
          let key = _ref10.key,
            value = _ref10.value;
          toDeleteCheck({
            selectedUtilityIndex,
            valueKey: key,
            prevValue: store.selectedUtilities[selectedUtilityIndex][key],
            newValue: value,
            utility: store.selectedUtilities[selectedUtilityIndex]
          });
          store.selectedUtilities[selectedUtilityIndex][key] = value;
        };
        data.forEach(datum => {
          const utility = datum.utility,
            values = datum.values;
          try {
            const selectedUtilityIndex = store.selectedUtilities.findIndex(i => i.utilityProviderId === utility.utilityProviderId && i.utilityId === utility.utilityId);
            if (selectedUtilityIndex !== -1) {
              values.forEach(val => handleValues(val, selectedUtilityIndex));
            } else {
              var _ref12, _valuesObject$vacantU;
              const valuesObject = {};
              values.forEach(_ref11 => {
                let key = _ref11.key,
                  value = _ref11.value;
                valuesObject[key] = value;
              });
              store.selectedUtilities.push(_objectSpread(_objectSpread({}, utility), {}, {
                id: utility.utilityId,
                utilityId: utility.utilityId,
                utilityProviderId: utility.utilityProviderId,
                utilityProviderName: utility.utilityProviderName,
                name: utility.name,
                billFrequencyType: (valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.billFrequencyType) || BILL_FREQUENCY[utility.billFrequencyType],
                allocationMethod: (valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.allocationMethod) || ALLOCATION_METHOD[utility.allocationMethod],
                buildingIds: toJS(utility === null || utility === void 0 ? void 0 : utility.buildingIds) || [],
                utilityCap: isValueNumber(valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.utilityCap) ? valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.utilityCap : isValueNumber(utility.utilityCap) ? utility.utilityCap : 0,
                utilityCAD: isValueNumber(valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.utilityCAD) ? valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.utilityCAD : isValueNumber(utility.utilityCAD) ? utility.utilityCAD : 5,
                goodWillDeduction: isValueNumber(valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.goodWillDeduction) ? valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.goodWillDeduction : isValueNumber(utility.goodWillDeduction) ? utility.goodWillDeduction : 5,
                isFetched: false,
                isEnabled: (valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.isEnabled) || utility.isEnabled,
                vacantUnitAllocation: (valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.vacantUnitAllocation) || utility.vacantUnitAllocation,
                vacantUnitUsage: (valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.vacantUnitUsage) || utility.vacantUnitUsage,
                vacantUnitOccupantsCount: (_ref12 = (_valuesObject$vacantU = valuesObject === null || valuesObject === void 0 ? void 0 : valuesObject.vacantUnitOccupantsCount) !== null && _valuesObject$vacantU !== void 0 ? _valuesObject$vacantU : utility.vacantUnitOccupantsCount) !== null && _ref12 !== void 0 ? _ref12 : null
              }));
            }
          } catch (error) {
            console.log('STORE updateUtilityConfig error', error);
          }
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    selectUtilities(_ref13) {
      let utilities = _ref13.utilities;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newUtilitiesToSelect = [];
        utilities.forEach(utility => {
          const existingUtility = store.selectedUtilities.find(item => (item.id === utility.id || item.utilityId === utility.id || item.utilityId === utility.utilityId) && item.utilityProviderId === utility.utilityProviderId);
          if (!existingUtility && utility.isEnabled) {
            var _utility$vacantUnitOc2;
            newUtilitiesToSelect.push(_objectSpread(_objectSpread({}, utility), {}, {
              id: utility.id,
              utilityProviderId: utility.utilityProviderId,
              utilityProviderName: utility.utilityProviderName,
              utilityId: utility.id || utility.utilityId,
              name: utility.utilityType || utility.name,
              billFrequencyType: utility.billFrequencyType,
              allocationMethod: utility.allocationMethod,
              buildingIds: toJS(utility.buildingIds) || [],
              utilityCap: isValueNumber(utility.utilityCap) ? utility.utilityCap : 0,
              utilityCAD: isValueNumber(utility.utilityCAD) ? utility.utilityCAD : isValueNumber(utility.commonAreaDeduction) ? utility.commonAreaDeduction : 5,
              goodWillDeduction: isValueNumber(utility.goodWillDeduction) ? utility.goodWillDeduction : 5,
              isEnabled: utility.isEnabled,
              vacantUnitAllocation: utility.vacantUnitAllocation,
              vacantUnitUsage: utility.vacantUnitUsage,
              vacantUnitOccupantsCount: (_utility$vacantUnitOc2 = utility.vacantUnitOccupantsCount) !== null && _utility$vacantUnitOc2 !== void 0 ? _utility$vacantUnitOc2 : null,
              isCustom: utility === null || utility === void 0 ? void 0 : utility.isCustom
            }));
          } else {
            store.selectedUtilities.map((selectedUtility, index) => {
              if (selectedUtility.utilityId === utility.id //&&
              // selectedUtility.utilityProviderId === provider.id
              ) {
                if (store.selectedUtilities[index].isFetched && !store.selectedUtilities[index].isEnabled) {
                  // add to remove object
                  store.addUtilityToDelete(store.selectedUtilities[index]);
                }
                store.selectedUtilities.splice(index, 1);
              }
            });
          }
        });
        store.selectedUtilities.push(...newUtilitiesToSelect);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    saveEditedCustomProvider(provider) {
      try {
        const utilities = cast(provider.utilities);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newUtilitiesToSelection = [];
        utilities.forEach(utility => {
          const existingUtilityIndex = store.selectedUtilities.findIndex(item => (item.id === utility.id || item.utilityId === utility.utilityId) && item.utilityProviderId === utility.utilityProviderId);
          if (existingUtilityIndex !== -1) {
            if (store.selectedUtilities[existingUtilityIndex].isFetched && !store.selectedUtilities[existingUtilityIndex].isEnabled) {
              // add to remove object
              store.addUtilityToDelete(store.selectedUtilities[existingUtilityIndex]);
            }
            Object.entries(utility).forEach(_ref14 => {
              let _ref15 = _slicedToArray(_ref14, 2),
                key = _ref15[0],
                value = _ref15[1];
              store.selectedUtilities[existingUtilityIndex][key] = value;
            });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            if (utility.isEnabled) {
              var _utility$vacantUnitOc3;
              newUtilitiesToSelection.push({
                id: utility.id,
                utilityProviderId: utility.utilityProviderId,
                utilityProviderName: utility.utilityProviderName,
                utilityId: utility.utilityId,
                name: utility.name,
                billFrequencyType: utility.billFrequencyType,
                allocationMethod: utility.allocationMethod,
                buildingIds: toJS(utility.buildingIds) || [],
                utilityCap: isValueNumber(utility.utilityCap) ? utility.utilityCap : 0,
                utilityCAD: isValueNumber(utility.utilityCAD) ? utility.utilityCAD : isValueNumber(utility.commonAreaDeduction) ? utility.commonAreaDeduction : 5,
                goodWillDeduction: isValueNumber(utility.goodWillDeduction) ? utility.goodWillDeduction : 5,
                isEnabled: utility.isEnabled,
                isCustom: utility.isCustom,
                vacantUnitAllocation: utility.vacantUnitAllocation,
                vacantUnitUsage: utility.vacantUnitUsage,
                vacantUnitOccupantsCount: (_utility$vacantUnitOc3 = utility.vacantUnitOccupantsCount) !== null && _utility$vacantUnitOc3 !== void 0 ? _utility$vacantUnitOc3 : null,
                isApPushEnabled: utility.isApPushEnabled
              });
            }
          }
        });
        store.selectedUtilities.push(...newUtilitiesToSelection);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setUtilitiesInfoAfterLoad(data) {
      let clearStore = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      try {
        if (clearStore) {
          store.utilityProviders = cast([]);
          store.selectedUtilities = cast([]);
        }
        data.map(item => {
          item.utilities.map(subItem => {
            try {
              const existingSelectedUtilityIndex = store.selectedUtilities.findIndex(selectedUtilityItem => selectedUtilityItem.id === subItem.id || selectedUtilityItem.utilityProviderId === item.utilityProviderId && selectedUtilityItem.utilityProviderName === item.utilityProviderName && selectedUtilityItem.utilityId === subItem.utilityId);
              if (existingSelectedUtilityIndex !== -1) {
                var _subItem$vacantUnitOc;
                store.selectedUtilities[existingSelectedUtilityIndex].id = subItem.id;
                store.selectedUtilities[existingSelectedUtilityIndex].billFrequencyType = BILL_FREQUENCY[subItem.billFrequencyType] || subItem.billFrequencyType;
                store.selectedUtilities[existingSelectedUtilityIndex].allocationMethod = ALLOCATION_METHOD[subItem.allocationMethod] || subItem.allocationMethod;
                store.selectedUtilities[existingSelectedUtilityIndex].buildingIds = subItem.buildingIds;
                store.selectedUtilities[existingSelectedUtilityIndex].utilityCap = subItem.commonAreaDeduction;
                store.selectedUtilities[existingSelectedUtilityIndex].utilityCAD = isValueNumber(subItem.commonAreaDeduction) ? subItem.commonAreaDeduction : 5;
                store.selectedUtilities[existingSelectedUtilityIndex].utilityTypeId = subItem.utilityTypeId;
                store.selectedUtilities[existingSelectedUtilityIndex].isFetched = true;
                store.selectedUtilities[existingSelectedUtilityIndex].isEnabled = true;
                store.selectedUtilities[existingSelectedUtilityIndex].isCustom = subItem === null || subItem === void 0 ? void 0 : subItem.isCustom;
                store.selectedUtilities[existingSelectedUtilityIndex].vacantUnitAllocation = VACANT_UNIT_ALLOCATIONS[subItem.vacantUnitAllocation];
                store.selectedUtilities[existingSelectedUtilityIndex].vacantUnitUsage = VACANT_UNIT_USAGE[subItem.vacantUnitUsage];
                store.selectedUtilities[existingSelectedUtilityIndex].vacantUnitUsageLimit = subItem.vacantUnitUsageLimit * 10 * 10; // I love JS
                store.selectedUtilities[existingSelectedUtilityIndex].vacantUnitOccupantsCount = (_subItem$vacantUnitOc = subItem.vacantUnitOccupantsCount) !== null && _subItem$vacantUnitOc !== void 0 ? _subItem$vacantUnitOc : null;
              } else {
                var _subItem$vacantUnitOc2;
                store.selectedUtilities.push({
                  id: subItem.id,
                  utilityProviderId: item.utilityProviderId,
                  utilityProviderName: item.utilityProviderName,
                  utilityId: subItem.utilityId,
                  name: subItem.utilityType,
                  billFrequencyType: BILL_FREQUENCY[subItem.billFrequencyType] || subItem.billFrequencyType,
                  allocationMethod: ALLOCATION_METHOD[subItem.allocationMethod] || subItem.allocationMethod,
                  buildingIds: subItem.buildingIds,
                  utilityCap: subItem.commonAreaDeduction,
                  utilityCAD: isValueNumber(subItem.commonAreaDeduction) ? subItem.commonAreaDeduction : 5,
                  goodWillDeduction: isValueNumber(subItem.goodWillDeduction) ? subItem.goodWillDeduction : 5,
                  vacantUnitAllocation: VACANT_UNIT_ALLOCATIONS[subItem.vacantUnitAllocation],
                  vacantUnitUsage: VACANT_UNIT_USAGE[subItem.vacantUnitUsage],
                  vacantUnitUsageLimit: subItem.vacantUnitUsageLimit * 10 * 10,
                  // I love JS
                  vacantUnitOccupantsCount: (_subItem$vacantUnitOc2 = subItem.vacantUnitOccupantsCount) !== null && _subItem$vacantUnitOc2 !== void 0 ? _subItem$vacantUnitOc2 : null,
                  utilityTypeId: subItem.utilityTypeId,
                  isFetched: true,
                  isEnabled: true,
                  isCustom: subItem === null || subItem === void 0 ? void 0 : subItem.isCustom
                });
              }
            } catch (e) {
              console.log('setUtilitiesInfoAfterLoad Error', e);
            }
          });
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    addUtilityProviderToDelete(data) {
      try {
        if (store.utilityProvidersToDelete.findIndex(provider => provider.id === data.id) === -1) {
          store.utilityProvidersToDelete.push(JSON.parse(JSON.stringify(data)));
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    wipeUtilityProviderToDelete() {
      try {
        store.utilityProvidersToDelete = cast([]);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    addUtilityToDelete(data) {
      try {
        if (store.selectedUtilitiesToDelete.findIndex(utility => utility.id === data.id) === -1) {
          store.selectedUtilitiesToDelete.push(JSON.parse(JSON.stringify(data)));
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeUtilityToDelete(utility) {
      try {
        const exitingToDeleteIndex = store.selectedUtilitiesToDelete.findIndex(util => util.id === utility.id);
        if (exitingToDeleteIndex !== -1) {
          store.selectedUtilitiesToDelete.splice(exitingToDeleteIndex, 1);
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    wipeUtilityToDelete() {
      try {
        store.selectedUtilitiesToDelete = cast([]);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    /*
      NOTE: Amenities Section
     */
    setAvailableCommonAreaAmenities(data) {
      try {
        store.availableCommonAreaAmenities = cast(data.map(i => Object.assign(i, {
          id: i.id,
          commonAreaAmenityId: i.id
        })));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSelectedAmenities(amenity) {
      try {
        if (!store.selectedAmenities.find(item => item.isFetched && item.id === amenity.id || !item.isFetched && item.commonAreaAmenityId === amenity.commonAreaAmenityId)) {
          store.selectedAmenities.push(JSON.parse(JSON.stringify(amenity)));
        } else if (store.selectedAmenities.find(item => !item.isFetched && item.commonAreaAmenityId === amenity.commonAreaAmenityId)) {
          const matchingAmenityIndex = store.selectedAmenities.findIndex(item => !item.isFetched && item.commonAreaAmenityId === amenity.commonAreaAmenityId);
          if (matchingAmenityIndex !== -1) {
            store.selectedAmenities[matchingAmenityIndex].id = amenity.id;
            store.selectedAmenities[matchingAmenityIndex].isFetched = amenity.isFetched;
          }
        }
      } catch (error) {
        console.error('STORE ERROR setSelectedAmenities > ', error);
      }
    },
    setFetchedAmenities(amenities) {
      try {
        amenities.forEach(amenity => {
          const matchingAmenity = store.availableCommonAreaAmenities.find(item => item.id === amenity.id || item.commonAreaAmenityId === amenity.commonAreaAmenityId);
          if (matchingAmenity) {
            const fetchedAmenityToStore = _objectSpread(_objectSpread({}, matchingAmenity), {}, {
              id: amenity.id,
              isFetched: true
            });
            store.setSelectedAmenities(fetchedAmenityToStore);
            store.setSelectedAmenityOption(fetchedAmenityToStore, amenity.commonAreaAmenityOptionId);
          }
        });
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeSelectedAmenity(amenity) {
      try {
        if (store.selectedAmenities.find(item => item.id === amenity.id)) {
          store.selectedAmenities = store.selectedAmenities.filter(item => item.id !== amenity.id);
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setSelectedAmenityOption(amenity, optionID) {
      try {
        const index = store.selectedAmenities.findIndex(amenityItem => amenityItem.id === amenity.id);
        if (index !== -1) {
          var _store$availableCommo;
          store.selectedAmenities[index].amenitySelectedOption = optionID;
          store.selectedAmenities[index].amenitySelectedOptionName = (_store$availableCommo = store.availableCommonAreaAmenities.find(amenityItem => amenityItem.id === amenity.id || amenityItem.id === amenity.commonAreaAmenityId).commonAreaAmenityOptions.find(option => option.id === optionID)) === null || _store$availableCommo === void 0 ? void 0 : _store$availableCommo.name;
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    replaceSelectedAmenity(replacingAmenityId, amenityIdToReplace) {
      try {
        const index = store.selectedAmenities.findIndex(amenityItem => amenityItem.id === amenityIdToReplace);
        if (index !== -1) {
          const availableAmenity = store.availableCommonAreaAmenities.find(i => i.id === replacingAmenityId);
          if (store.selectedAmenities[index].isFetched) {
            store.addCADToDelete(store.selectedAmenities[index]);
          }
          store.selectedAmenities[index] = Object.assign(JSON.parse(JSON.stringify(availableAmenity)), {
            isSaving: true
          });
          if (store.selectedAmenities[index].commonAreaAmenityOptions[0]) {
            store.selectedAmenities[index].amenitySelectedOption = store.selectedAmenities[index].commonAreaAmenityOptions[0].id;
            store.selectedAmenities[index].amenitySelectedOptionName = store.selectedAmenities[index].commonAreaAmenityOptions[0].name;
          }
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAmenitySavingMode(amenity, commonAreaAmenityId, state) {
      try {
        const index = store.selectedAmenities.findIndex(amenityItem => amenityItem.id === (amenity === null || amenity === void 0 ? void 0 : amenity.id) || amenityItem.id === commonAreaAmenityId);
        if (index !== -1) store.selectedAmenities[index].isSaving = state;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    // NOTE: Common area amenity
    addCADToDelete(data) {
      try {
        if (store.selectedAmenitiesToDelete.findIndex(provider => provider.id === data.id) === -1) {
          store.selectedAmenitiesToDelete.push(JSON.parse(JSON.stringify(data)));
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    wipeCADToDelete() {
      try {
        store.selectedAmenitiesToDelete = cast([]);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setRentGlId(data) {
      try {
        store.rentGlAccountExternalId = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setGlobalGlId(data) {
      try {
        store.globalGlAccountExternalId = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAccountsPayableGlId(data) {
      try {
        store.accountsPayableGlAccountExternalId = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setPropertyUtilityGlAccounts(data) {
      try {
        store.propertyUtilityGlAccounts = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get getPropertySqFootage() {
    return store.sqFootage;
  },
  get getUtilityConfigurator() {
    return store["".concat(UTILITY_CONFIGURATION_TYPES.UtilityConfigurator)];
  },
  get getCustomUtilityProviderConfigurator() {
    return store["".concat(UTILITY_CONFIGURATION_TYPES.CustomUtilityProviderConfigurator)];
  },
  get getEditCustomUtilityProviderConfigurator() {
    return store["".concat(UTILITY_CONFIGURATION_TYPES.EditCustomUtilityProviderConfigurator)];
  },
  get utilityIds() {
    const utilityIds = [];
    toJS(store.utilityProviders).forEach(i => i.utilities.forEach(j => utilityIds.push(j.id)));
    return utilityIds;
  },
  // get getCustomUtilityProviderConfiguratorEnabled() {
  //   return store.customUtilityProviderConfigurator?.utilities.filter(
  //     i => i?.isEnabled
  //   );
  // },
  get getUtilityProviders() {
    return store.utilityProviders;
  },
  getPropertyProviderSetup(providerId) {
    const provider = store.utilityProviders.find(providerItem => providerItem.id === providerId);
    const enabledProviderUtilities = store.selectedUtilities.filter(utility => providerId === utility.utilityProviderId);
    const result = provider === null || provider === void 0 ? void 0 : provider.utilities.map(i => {
      return {
        utilityProviderId: provider.id,
        utilityProviderName: provider.name,
        utilityId: i.id,
        isCustom: i.isCustom,
        name: i.name
      };
    }).filter(i => enabledProviderUtilities.filter(util => util.utilityProviderId === provider.id).findIndex(si => si.utilityId === i.utilityId) === -1);
    const finalObject = toJS(provider);
    finalObject.utilities = [enabledProviderUtilities.map(i => toJS(i)), result].flat();
    return finalObject;
  },
  getPropertyUtilitySetupByProvider(providerId) {
    const provider = store.utilityProviders.find(providerItem => providerItem.id === providerId);
    const enabledProviderUtilities = store.selectedUtilities.filter(utility => providerId === utility.utilityProviderId);
    const result = provider === null || provider === void 0 ? void 0 : provider.utilities.map(i => {
      return _objectSpread(_objectSpread({}, i), {}, {
        utilityProviderId: provider.id,
        utilityProviderName: provider.name,
        utilityId: i.id,
        name: i.name
      });
    }).filter(i => enabledProviderUtilities.filter(util => util.utilityProviderId === provider.id).findIndex(si => si.utilityId === i.utilityId) === -1);
    return [enabledProviderUtilities, result].flat().sort(i => i === null || i === void 0 ? void 0 : i.name);
  }
}));